/**
 * Analytics Filter Builder Types
 *
 * Supports dynamic filter rules with property, operator, and value(s).
 * Rules are combined with AND logic.
 */
/**
 * Available filter fields based on WebsiteEvent properties
 */
const AnalyticsFilterFields = [
    'campaignId',
    'variantId',
    'websiteId',
    'device',
    'os',
    'browser',
    'country',
    'region',
    'city',
    'adNetworkProvider',
    'referrerDomain',
    'origin',
    'search',
    'utmSource',
    'utmMedium',
    'utmContent',
    'adAccountId',
    'adCampaignId',
    'adGroupId',
    'adId',
    'keyword',
    'network',
    'matchType',
    'placement',
    'path',
    'environment',
];
/**
 * Field metadata configuration
 */
const AnalyticsFilterFieldsMeta = [
    {
        field: 'campaignId',
        label: 'Page/Quiz',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side campaigns
    },
    {
        field: 'variantId',
        label: 'Variant',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side variants
    },
    {
        field: 'websiteId',
        label: 'Experiment Variation',
        type: 'select',
        dynamicOptions: false, // Fetched from client-side websites with experiments
    },
    {
        field: 'device',
        label: 'Device',
        type: 'enum',
        staticOptions: [
            { value: 'Mobile', label: 'Mobile' },
            { value: 'Desktop', label: 'Desktop' },
            { value: 'Tablet', label: 'Tablet' },
        ],
    },
    {
        field: 'os',
        label: 'Operating System',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'browser',
        label: 'Browser',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'country',
        label: 'Country',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'region',
        label: 'Region',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'city',
        label: 'City',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adNetworkProvider',
        label: 'Ad Network',
        type: 'enum',
        staticOptions: [
            { value: 'google', label: 'Google' },
            { value: 'facebook', label: 'Facebook' },
            { value: 'microsoft', label: 'Microsoft' },
            { value: 'tiktok', label: 'TikTok' },
            { value: 'linkedin', label: 'LinkedIn' },
            { value: 'pinterest', label: 'Pinterest' },
            { value: 'twitter', label: 'Twitter' },
            { value: 'snapchat', label: 'Snapchat' },
            { value: 'taboola', label: 'Taboola' },
            { value: 'outbrain', label: 'Outbrain' },
            { value: 'reddit', label: 'Reddit' },
            { value: 'applovin', label: 'AppLovin' },
            { value: 'unknown', label: 'Unknown' },
        ],
    },
    {
        field: 'referrerDomain',
        label: 'Referrer Domain',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'origin',
        label: 'Origin',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'search',
        label: 'URL Params',
        type: 'string',
        dynamicOptions: false,
    },
    {
        field: 'utmSource',
        label: 'UTM Source',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'utmMedium',
        label: 'UTM Medium',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'utmContent',
        label: 'UTM Content',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adAccountId',
        label: 'Ad Account ID',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adCampaignId',
        label: 'Ad Campaign ID',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adGroupId',
        label: 'Ad Group ID',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'adId',
        label: 'Ad ID',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'keyword',
        label: 'Keyword',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'network',
        label: 'Ad Network Channel',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'matchType',
        label: 'Match Type',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'placement',
        label: 'Placement',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'path',
        label: 'Page Path',
        type: 'string',
        dynamicOptions: true,
    },
    {
        field: 'environment',
        label: 'Environment',
        type: 'enum',
        staticOptions: [
            { value: 'production', label: 'Production' },
            { value: 'test', label: 'Test' },
        ],
    },
];
/**
 * Filter operators
 */
const AnalyticsFilterOperators = [
    'is',
    'isNot',
    'isAnyOf',
    'isNotAnyOf',
    'contains',
    'doesNotContain',
    'startsWith',
    'isEmpty',
    'isNotEmpty',
    'greaterThan',
    'lessThan',
];
/**
 * Operators available for each field type
 */
const OperatorsByFieldType = {
    string: [
        'isAnyOf',
        'isNotAnyOf',
        'contains',
        'doesNotContain',
        'startsWith',
        'isEmpty',
        'isNotEmpty',
    ],
    select: ['isAnyOf', 'isNotAnyOf'],
    boolean: ['is', 'isNot'],
    enum: ['isAnyOf', 'isNotAnyOf'],
};
/**
 * Operator display labels
 */
const OperatorLabels = {
    is: 'equals',
    isNot: 'not equals',
    isAnyOf: 'is any of',
    isNotAnyOf: 'is not any of',
    contains: 'contains',
    doesNotContain: 'does not contain',
    startsWith: 'starts with',
    isEmpty: 'is empty',
    isNotEmpty: 'is not empty',
    greaterThan: 'greater than',
    lessThan: 'less than',
};
const CustomPropertyOperators = [
    'is',
    'isNot',
    'isAnyOf',
    'isNotAnyOf',
    'contains',
    'doesNotContain',
    'startsWith',
    'greaterThan',
    'lessThan',
    'isEmpty',
    'isNotEmpty',
];
/**
 * Operators that don't require a value
 */
const NoValueOperators = [
    'isEmpty',
    'isNotEmpty',
];
/**
 * Operators that accept multiple values
 */
const MultiValueOperators = [
    'isAnyOf',
    'isNotAnyOf',
];
const CustomPropertyMultiValueOperators = [
    'isAnyOf',
    'isNotAnyOf',
    'contains',
    'startsWith',
];
/**
 * Operators that require free-text input (no dropdown options)
 */
const TextOperators = [
    'is',
    'isNot',
    'contains',
    'doesNotContain',
    'startsWith',
    'greaterThan',
    'lessThan',
];
/**
 * Empty/default filter state
 */
const EmptyAnalyticsFilters = {
    logic: 'and',
    rules: [],
};

export { AnalyticsFilterFields, AnalyticsFilterFieldsMeta, AnalyticsFilterOperators, CustomPropertyMultiValueOperators, CustomPropertyOperators, EmptyAnalyticsFilters, MultiValueOperators, NoValueOperators, OperatorLabels, OperatorsByFieldType, TextOperators };
//# sourceMappingURL=analytics-filter.esm.js.map
